import { apiFetch } from '../../services/api';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';

interface SoundConfig {
    event_key: string;
    sound_url: string | null;
    volume: number;
}

export const GamenicsSoundManagement = () => {
    const [sounds, setSounds] = useState<SoundConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

    useEffect(() => {
        loadSounds();
    }, []);

    const loadSounds = async () => {
        try {
            setLoading(true);
            const res = await apiFetch('/api/admin/sounds');
            const data = await res.json();
            if (data.success) {
                setSounds(data.data || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const playPreview = (url: string) => {
        if (!url) return;
        if (audioRef.current) {
            audioRef.current.src = url;
            audioRef.current.play().catch(e => console.warn('Preview blocked:', e));
        } else {
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.play().catch(e => console.warn('Preview blocked:', e));
        }
    };

    const handleFileUpload = async (eventKey: string, file: File) => {
        if (!file.type.startsWith('audio/')) {
            alert('音声ファイルを選択してください。');
            return;
        }

        const formData = new FormData();
        formData.append('event', eventKey);
        formData.append('file', file);

        try {
            const res = await apiFetch('/api/admin/sounds/upload', {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                alert('アップロード完了しました。');
                loadSounds(); // Reload the list
            } else {
                alert('エラー: ' + data.error);
            }
        } catch (e) {
            console.error(e);
            alert('アップロードに失敗しました。');
        }
    };

    if (loading) {
        return <div className="text-ink-2 p-4">読み込み中...</div>;
    }

    return (
        <div className="bg-cream-2 border-2 border-ink/15 p-6 rounded-xl space-y-6">
            <h2 className="text-xl font-bold text-ink mb-4">効果音管理 (Gamenics)</h2>
            <p className="text-sm text-ink-2 mb-6">
                各アクション時に再生される効果音（.mp3, .wav等）を管理します。<br />
                ※ プログラム合成音の使用はガイドライン違反となるため、必ず音声ファイルを登録してください。
            </p>

            <div className="space-y-4">
                {sounds.map((sound) => (
                    <div key={sound.event_key} className="flex items-center justify-between p-4 bg-cream rounded-lg border-2 border-ink/15">
                        <div className="flex-1">
                            <h3 className="text-ink font-bold">{sound.event_key}</h3>
                            <p className="text-xs text-ink-3 font-mono mt-1">
                                {sound.sound_url || '未設定'}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            {sound.sound_url && (
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="border-primary text-primary hover:bg-primary hover:text-black"
                                    onClick={() => playPreview(sound.sound_url!)}
                                >
                                    ▶ プレビュー
                                </Button>
                            )}
                            <div className="flex flex-col gap-1 items-end">
                                <input 
                                    type="file" 
                                    accept="audio/*"
                                    className="hidden"
                                    ref={(el) => fileInputRefs.current[sound.event_key] = el}
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            handleFileUpload(sound.event_key, e.target.files[0]);
                                        }
                                    }}
                                />
                                <Button 
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fileInputRefs.current[sound.event_key]?.click()}
                                >
                                    アップロード
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
